import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EditNoteIcon from '@mui/icons-material/EditNote';

interface JourneyMapToolbarProps {
  name?: string;
  publicationStatus?: string;
  itemCount?: number;
  linkCount?: number;
  pathGroupCount?: number;
  isDirty?: boolean;
  isNew?: boolean;
  version?: number;
  isActive?: boolean;
  typeName?: string;
  mapId?: number | null;
  isSaving?: boolean;
  canSave?: boolean;
  validationMessage?: string | null;
  onSave?: () => void;
}

function JourneyMapToolbar({
                             name,
                             publicationStatus,
                             itemCount = 0,
                             linkCount = 0,
                             pathGroupCount = 0,
                             isDirty = false,
                             isNew = true,
                             version = 1,
                             isActive = true,
                             typeName,
                             mapId,
                             isSaving = false,
                             canSave = true,
                             validationMessage,
                             onSave,
                           }: JourneyMapToolbarProps) {
  const trimmedName = (name || '').trim();
  const trimmedTypeName = (typeName || '').trim();

  const requiredValidationMessage = useMemo(() => {
    if (!trimmedName && !trimmedTypeName) {
      return 'Journey Map Name and Journey Type are required before saving.';
    }
    if (!trimmedName) {
      return 'Journey Map Name is required before saving.';
    }
    if (!trimmedTypeName) {
      return 'Journey Type is required before saving.';
    }
    return null;
  }, [trimmedName, trimmedTypeName]);

  const finalValidationMessage = validationMessage || requiredValidationMessage;
  const canActuallySave =
    !!trimmedName && !!trimmedTypeName && canSave && !isSaving;

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AccountTreeIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Journey Map Builder
            </Typography>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={onSave}
            disabled={!canActuallySave}
          >
            {isSaving ? 'Saving...' : publicationStatus === 'published' ? 'Save & Publish' : 'Save Draft'}
          </Button>
        </Stack>
      </Stack>

      <Divider />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          icon={<EditNoteIcon />}
          label={`Name: ${trimmedName || 'Required'}`}
          color={trimmedName ? 'primary' : 'warning'}
          variant="outlined"
        />
        <Chip
          label={`Type: ${trimmedTypeName || 'Required'}`}
          color={trimmedTypeName ? 'primary' : 'warning'}
          variant="outlined"
        />
        <Chip
          label={`Status: ${publicationStatus || 'draft'}`}
          color={publicationStatus === 'published' ? 'success' : 'default'}
          variant="outlined"
        />
        <Chip label={`Items: ${itemCount}`} variant="outlined" />
        <Chip label={`Links: ${linkCount}`} variant="outlined" />
        <Chip label={`Path Groups: ${pathGroupCount}`} variant="outlined" />
        <Chip
          label={isDirty ? 'Unsaved Changes' : 'Saved'}
          color={isDirty ? 'warning' : 'success'}
        />
        <Chip
          label={isNew ? 'New map' : 'Existing map'}
          variant="outlined"
        />
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Typography variant="body2">
          <strong>Version:</strong> {version || 1}
        </Typography>
        <Typography variant="body2">
          <strong>Active:</strong> {isActive ? 'Yes' : 'No'}
        </Typography>
        {mapId ? (
          <Typography variant="body2">
            <strong>Map ID:</strong> {mapId}
          </Typography>
        ) : null}
      </Stack>

      {finalValidationMessage ? (
        <Typography variant="body2" color="warning.main">
          {finalValidationMessage}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default JourneyMapToolbar;